import {
  CloudDownload as ScrapeIcon,
  MusicNote as MusicIcon,
  Search as SearchIcon,
  Shuffle as ShuffleIcon,
  TrendingUp as TrendingIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { ChangeEvent, useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ScrapeModal from "../Components/ScrapeModal";
import { SongModel } from "../Entities/SongModel";
import { apiFetchRandomSongs, apiFetchSongs } from "../utils/useDb";
import { useShuffle } from "../utils/useShuffle";
import { useUrlParam } from "../utils/useUrlParams";

export default function Explore() {
  const navigate = useNavigate();
  // Data state
  const [songs, setSongs] = useState<SongModel[]>([]);
  const [totalSongCount, setTotalSongCount] = useState<number>(0);
  const [isScrapeOpen, setIsScrapeOpen] = useState<boolean>(false);

  // Global State
  const [shuffleIndex, setShuffle] = useShuffle(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [startFrom, setStartFrom] = useUrlParam<number>(
    "start",
    (value: number) => (value <= 0 ? null : value.toString()),
    (value: string | null) => Number(value) || 0
  );
  const [searchQuery, setSearchQueryParam] = useUrlParam<string>(
    "search",
    (value: string) => value || null,
    (value: string | null) => (value ? value : "")
  );

  // Search fields private state
  const [searchValue, setSearchValue] = useState<string>(searchQuery);
  const theme = useTheme();

  const getDifficultyColor = (d: string | undefined) => {
    switch (d) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
      case "expert":
        return "error";
      default:
        return "default";
    }
  };

  const loadSongs = useCallback(async () => {
    setIsLoading(true);
    try {
      if (shuffleIndex > 0) {
        const randomSongs = await apiFetchRandomSongs(50);
        setSongs(randomSongs);
        setTotalSongCount(randomSongs.length);
      } else {
        const response = await apiFetchSongs({
          search: searchQuery || undefined,
          start: startFrom,
          limit: 50,
        });
        setSongs(response.items || []);
        setTotalSongCount(response.total || 0);
      }
    } catch (err) {
      console.error("Error loading songs from API:", err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, startFrom, shuffleIndex]);

  useEffect(() => {
    loadSongs();
  }, [loadSongs]);

  const resetParams = ({
    is_search = false,
    is_shuffle = false,
  }: {
    is_search?: boolean;
    is_shuffle?: boolean;
  }) => {
    setIsSearch(is_search);
    if (!is_search) {
      setSearchQueryParam("");
    } else {
      setSearchQueryParam(searchQuery);
    }
    if (is_shuffle) {
      setStartFrom(0);
    } else {
      setShuffle(false);
    }
  };

  const submitSearchForm = async () => {
    resetParams({ is_search: searchValue.trim() !== "" });
    if (searchValue !== searchQuery) {
      setStartFrom(0);
    }
    setSearchQueryParam(searchValue.trim());
  };

  const submitShuffle = () => {
    resetParams({ is_shuffle: true });
    setShuffle(true);
  };

  const getOriginalWebsite = (url: string) => {
    if (!url) return "ChordMaster";
    if (url.includes("tab4u")) return "Tab4U";
    if (url.includes("ultimate-guitar")) return "Ultimate Guitar";
    return "ChordMaster";
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              component="h1"
              fontWeight="bold"
              color="black"
            >
              Discover Amazing{" "}
              <Box
                component="span"
                sx={{
                  background: "linear-gradient(90deg, #1976d2, #5e35b1)",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                }}
              >
                Guitar Songs
              </Box>
            </Typography>
            <Typography variant="h6" color="textSecondary" mt={2}>
              Explore chords, lyrics, and tabs. Perfect for beginners
              and pros alike.
            </Typography>
          </Box>
        </motion.div>

        {/* Search & Filters */}
        <Card
          variant="outlined"
          sx={{ mb: 4, backdropFilter: "blur(4px)", py: 2 }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, lg: 6 }}>
                <Box position="relative" display="flex" alignItems="center">
                  <SearchIcon
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 12,
                      transform: "translateY(-50%)",
                      color: "text.disabled",
                      pointerEvents: "none",
                    }}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search songs, artists, or URL..."
                    value={searchValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSearchValue(e.target.value)
                    }
                    InputProps={{ sx: { pl: 5, py: 0.5 } }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") submitSearchForm();
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 4, lg: 2 }}>
                <button
                  style={{
                    height: 48,
                    width: "100%",
                    padding: "0 16px",
                    borderRadius: 6,
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "0.95rem",
                  }}
                  onClick={submitSearchForm}
                >
                  Search
                </button>
              </Grid>
              <Grid size={{ xs: 4, lg: 2 }}>
                <button
                  style={{
                    height: 48,
                    width: "100%",
                    padding: "0 16px",
                    borderRadius: 6,
                    border: "none",
                    background: "#5e35b1",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    fontSize: "0.95rem",
                  }}
                  onClick={submitShuffle}
                >
                  <ShuffleIcon sx={{ fontSize: 20 }} />
                  Shuffle
                </button>
              </Grid>
              <Grid size={{ xs: 4, lg: 2 }}>
                <button
                  style={{
                    height: 48,
                    width: "100%",
                    padding: "0 16px",
                    borderRadius: 6,
                    border: "none",
                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    fontSize: "0.95rem",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
                  }}
                  onClick={() => setIsScrapeOpen(true)}
                >
                  <ScrapeIcon sx={{ fontSize: 20 }} />
                  Import Songs
                </button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Results Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h5" fontWeight="bold">
            {isSearch || searchQuery
              ? `Search Results (${totalSongCount})`
              : shuffleIndex > 0
              ? `Random Songs (${songs.length})`
              : `All Songs (${totalSongCount})`}
          </Typography>
          <Box display="flex" alignItems="center" color="text.secondary">
            <TrendingIcon fontSize="small" />
            <Typography variant="body2" ml={0.5}>
              Updated daily
            </Typography>
          </Box>
        </Box>

        {/* Song Cards */}
        {isLoading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        ) : songs.length > 0 ? (
          <>
            <Grid
              container
              spacing={2}
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              {songs.map((song, idx) => (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4 }}
                  key={song.id}
                  sx={{
                    transition: "0.3s",
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(idx * 0.03, 0.5) }}
                  >
                    <Card
                      component={Link}
                      to={`/Song?id=${song.id}`}
                      sx={{
                        textDecoration: "none",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        background: theme.palette.background.paper,
                        borderTop: song.owner && song.owner !== "system" ? "4px solid #7c3aed" : "1px solid #e2e8f0",
                        ":hover": {
                          boxShadow: 6,
                          transform: "translateY(-4px)",
                        },
                        transition: "0.2s",
                      }}
                    >
                      <CardHeader
                        action={
                          song.owner && song.owner !== "system" ? (
                            <Chip
                              label="My Song"
                              size="small"
                              sx={{
                                bgcolor: "#f5f3ff",
                                color: "#7c3aed",
                                fontWeight: 700,
                                border: "1px solid #ddd6fe",
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          ) : null
                        }
                        title={song.title}
                        subheader={
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            component="span"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate(`/Artist?name=${encodeURIComponent(song.artist)}`);
                            }}
                            sx={{
                              cursor: "pointer",
                              display: "inline-block",
                              "&:hover": {
                                color: "primary.main",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {song.artist}
                          </Typography>
                        }
                        titleTypographyProps={{
                          fontWeight: "bold",
                          variant: "h6",
                        }}
                      />
                      <CardContent sx={{ mt: "auto" }}>
                        <Box mb={1} display="flex" gap={1} flexWrap="wrap">
                          {song.difficulty && (
                            <Chip
                              label={song.difficulty}
                              color={getDifficultyColor(song.difficulty)}
                              size="small"
                            />
                          )}
                          {song.key && (
                            <Chip
                              label={`Key: ${song.key}`}
                              variant="outlined"
                              size="small"
                            />
                          )}
                          {song.url && (
                            <Chip
                              color="info"
                              size="small"
                              label={getOriginalWebsite(song.url)}
                            />
                          )}
                        </Box>
                        {song.tags && song.tags.length > 0 && (
                          <Box mb={1} display="flex" gap={0.5} flexWrap="wrap">
                            {song.tags.slice(0, 3).map((t) => (
                              <Chip
                                key={t}
                                label={t}
                                variant="outlined"
                                size="small"
                              />
                            ))}
                            {song.tags.length > 3 && (
                              <Chip
                                label={`+${song.tags.length - 3}`}
                                variant="outlined"
                                size="small"
                              />
                            )}
                          </Box>
                        )}
                        {song.created_at && (
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            color="text.secondary"
                            fontSize="0.75rem"
                            mt={1}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {new Date(song.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
            <Grid container justifyContent="center" my={5}>
              <Box display="flex" gap={2}>
                <button
                  disabled={startFrom <= 0}
                  onClick={() => setStartFrom(Math.max(0, startFrom - 50))}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 6,
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: startFrom <= 0 ? "not-allowed" : "pointer",
                    opacity: startFrom <= 0 ? 0.5 : 1,
                  }}
                >
                  Previous Page
                </button>
                <button
                  disabled={startFrom + 50 >= totalSongCount}
                  onClick={() => setStartFrom(startFrom + 50)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 6,
                    border: "none",
                    background: "#1976d2",
                    color: "#fff",
                    fontWeight: 600,
                    cursor:
                      startFrom + 50 >= totalSongCount ? "not-allowed" : "pointer",
                    opacity: startFrom + 50 >= totalSongCount ? 0.5 : 1,
                  }}
                >
                  Next Page
                </button>
              </Box>
            </Grid>
          </>
        ) : (
          <Card variant="outlined" sx={{ textAlign: "center", p: 6, borderRadius: 3 }}>
            <Box mb={2}>
              <MusicIcon color="disabled" sx={{ fontSize: 64 }} />
            </Box>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              No songs found
            </Typography>
            <Typography color="textSecondary" mb={3}>
              Try adjusting your search or click Shuffle to explore
            </Typography>
            <Box display="flex" justifyContent="center" gap={2}>
              <button
                style={{
                  height: 42,
                  padding: "0 20px",
                  borderRadius: 6,
                  border: "none",
                  background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "0.9rem",
                }}
                onClick={() => setIsScrapeOpen(true)}
              >
                <ScrapeIcon sx={{ fontSize: 18 }} />
                Import Songs
              </button>
            </Box>
          </Card>
        )}
      </Container>

      <ScrapeModal
        open={isScrapeOpen}
        onClose={() => setIsScrapeOpen(false)}
        onSuccess={() => loadSongs()}
        initialQuery={searchValue || searchQuery}
      />
    </Box>
  );
}

function SkeletonCard() {
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <CardHeader
        title={<Box sx={{ bgcolor: "grey.300", height: 24, width: "60%", borderRadius: 1 }} />}
        subheader={
          <Box sx={{ bgcolor: "grey.200", height: 16, width: "40%", mt: 1, borderRadius: 1 }} />
        }
      />
      <CardContent>
        <Box sx={{ bgcolor: "grey.200", height: 16, mb: 1, borderRadius: 1 }} />
        <Box display="flex" gap={1}>
          <Box sx={{ bgcolor: "grey.300", height: 24, width: 64, borderRadius: 1 }} />
          <Box sx={{ bgcolor: "grey.300", height: 24, width: 80, borderRadius: 1 }} />
        </Box>
      </CardContent>
    </Card>
  );
}
